import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { MapPin, Clock, Phone, Mail, Loader2, Send } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INFO = [
  { icon: MapPin, label: "Service Area", value: "Fully mobile — we come to your home or workplace" },
  { icon: Clock, label: "Hours", value: "Mon – Sat · 8:00 AM – 3:00 PM · By appointment only" },
  { icon: Phone, label: "Direct Line", value: "+61 417 406 332", href: "tel:+61417406332" },
  { icon: Mail, label: "Email", value: "info@elitemobiledetail.com.au", href: "mailto:info@elitemobiledetail.com.au" },
];

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#0C0E14] px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none transition-[border-color,box-shadow] duration-300 focus:border-[#FFB703]/60 focus:shadow-[0_0_0_3px_rgba(255,183,3,0.12)]";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", vehicle: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        vehicle: form.vehicle || null,
        notes: form.notes || null,
      });
      toast.success("Message received", { description: "Our master detailers will reply within 60 minutes." });
      setForm({ name: "", email: "", phone: "", vehicle: "", notes: "" });
    } catch {
      toast.error("Could not send your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="border-t border-white/10 bg-[#0C0E14]/60 py-20 sm:py-28 lg:py-32">
      <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 48 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-[#FFB703]">04 // Contact</p>
          <h2 className="font-display text-[clamp(1.4rem,6vw,2.25rem)] font-extrabold uppercase tracking-tight text-white sm:text-4xl lg:text-5xl">
            Book Your <span className="text-[#FFB703]">Detail</span>
          </h2>
          <p className="mt-5 max-w-md text-sm leading-relaxed text-slate-400 sm:text-base">
            Every vehicle is inspected on arrival before we confirm a final quote. Reach out — we respond within 60 minutes during business hours.
          </p>
          <div className="mt-10 flex flex-col gap-6">
            {INFO.map((item) => (
              <div key={item.label} className="flex items-start gap-4" data-testid={`contact-info-${item.label.toLowerCase().replace(/\s/g, "-")}`}>
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-[#12141C]">
                  <item.icon className="h-4 w-4 text-[#FFB703]" />
                </span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{item.label}</p>
                  {item.href ? (
                    <a href={item.href} data-testid={`contact-link-${item.label.toLowerCase().replace(/\s/g, "-")}`} className="mt-1 block text-sm text-slate-200 transition-colors hover:text-[#FFB703]">{item.value}</a>
                  ) : (
                    <p className="mt-1 text-sm text-slate-200">{item.value}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 48 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col gap-3 rounded-2xl border border-white/10 bg-[#12141C]/90 p-6 shadow-[0_0_60px_rgba(0,0,0,0.4)] backdrop-blur-md sm:p-8"
        >
          <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">General Enquiry</p>
          <input required data-testid="contact-input-name" placeholder="Full name *" value={form.name} onChange={setField("name")} className={inputCls} />
          <div className="grid gap-3 sm:grid-cols-2">
            <input required type="email" data-testid="contact-input-email" placeholder="Email *" value={form.email} onChange={setField("email")} className={inputCls} />
            <input data-testid="contact-input-phone" placeholder="Phone" value={form.phone} onChange={setField("phone")} className={inputCls} />
          </div>
          <input data-testid="contact-input-vehicle" placeholder="Vehicle year / make / model" value={form.vehicle} onChange={setField("vehicle")} className={inputCls} />
          <textarea data-testid="contact-input-message" placeholder="How can we help? *" required rows={5} value={form.notes} onChange={setField("notes")} className={`${inputCls} resize-none`} />
          <button
            type="submit"
            disabled={submitting}
            data-testid="contact-submit-button"
            className="group mt-2 flex items-center justify-center gap-2 rounded-xl bg-[#FFB703] py-4 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-[#090A0E] transition-[background-color,box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#FFA200] hover:shadow-[0_0_36px_rgba(255,183,3,0.4)] disabled:opacity-60"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
            Send Message
          </button>
        </motion.form>
      </div>
    </section>
  );
}
